import React, { useMemo, useState, createContext, useContext, useCallback, useEffect } from 'react'
import useSWR, { type KeyedMutator } from 'swr'

import destroyCart from '@lib/cart/destroy-cart'
import getCartId from '@lib/cart/get-cart-id'
import shouldUseDiscount from '@lib/discount/should-use-discount'
import fetcher from '@lib/fetcher'
import cartDiscountCodesUpdateMutation from '@lib/mutations/cart-discount-codes-update'
import cartGiftCardCodesUpdateMutation from '@lib/mutations/cart-gift-card-codes-update'
import getCartQuery from '@lib/queries/get-cart-query'
import type { UpdateCartAttributesInput } from '@lib/queries/update-cart-attributes'
import updateCartAttributes from '@lib/queries/update-cart-attributes'
import type { CartErrorCode, CartItem, Cart as CartType } from '@lib/types/cart'
import { normalizeCart, normalizeCartItem } from '@lib/utils/normalizers'

import addItem from '@lib/cart/add-item'

export type GetCartReturn = {
	cart: CartType | null | undefined
	items: CartItem[] | null | undefined
	itemCount: number
}

type GetCartDiscountCodesUpdateReturn = {
	cart: CartType
	userErrors: { code: CartErrorCode; field: string[]; message: string }[]
}

type GetCartGiftCardCodesUpdateReturn = {
	cart: CartType
	userErrors: { code: CartErrorCode; field: string[]; message: string }[]
	warnings: { code: string; message: string }[]
}

type CartContextType = {
	cart: CartType | null | undefined
	items: CartItem[] | null | undefined
	itemCount: number
	loading: boolean
	revalidate: KeyedMutator<GetCartReturn>
	appliedDiscountCodes: string[]
	appliedGiftCards: CartType['appliedGiftCards']
	discountAllocations: CartType['discountAllocations']
	addDiscountCode: (code: string) => Promise<{ applied: boolean; errors: any | null }>
	removeDiscountCode: (code: string) => Promise<void>
	getCart: () => Promise<GetCartReturn>
	addSurveyAnswerToCartAttributes: (
		surveyAnswers: { question_id: string; answer: string }[]
	) => Promise<null>
	addExperimentVariantToCart: (key: string, value: string) => Promise<null>
	isCartOpen: boolean
	openCart: () => void
	closeCart: () => void
	toggleCart: () => void
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
	const { data, isValidating: loading, error, mutate } = useSWR<GetCartReturn>('cart', getCart)
	const [isCartOpen, setIsCartOpen] = useState(false)

	const { cart, items } = data ?? {}
	const itemCount = data?.itemCount ?? 0

	const appliedDiscountCodes = useMemo(
		() =>
			data?.cart?.discountCodes && data.cart.discountCodes.length > 0
				? data.cart.discountCodes.filter(({ applicable }) => applicable).map(({ code }) => code)
				: [],
		[data]
	)

	const openCart = useCallback(() => {
		setIsCartOpen(true)
	}, [])

	const closeCart = useCallback(() => {
		setIsCartOpen(false)
	}, [])

	const toggleCart = useCallback(() => {
		setIsCartOpen(!isCartOpen)
	}, [isCartOpen])

	async function getCart(): Promise<GetCartReturn> {
		const cartId = getCartId()

		if (cartId) {
			const cartData = await fetcher({
				query: getCartQuery,
				variables: { cartId }
			})

			return formatCart(cartData)
		}

		return { cart: null, items: null, itemCount: 0 }
	}

	const getCartCallback = useCallback(getCart, [])

	const addSurveyAnswerToCartAttributesCallback = useCallback(
		async (surveyAnswers: { question_id: string; answer: string }[]) => {
			if (cart && items && items.length) {
				try {
					const updateCartAttributesObject: UpdateCartAttributesInput = {
						cartId: cart.id,
						attributes: surveyAnswers.map((sa) => ({
							// add 'survey_' in the beginning of the key to differentiate survey answers from other attributes
							key: `survey_${sa.question_id}`,
							// '-' means blank answer
							value: sa.answer || '-'
						}))
					}

					await fetcher({
						query: updateCartAttributes,
						variables: updateCartAttributesObject
					})
				} catch (err: any) {
					console.error('Unable to add Survey Answer to Shopify cart attributes: ', err)
				}
			}

			return null
		},
		[cart, items]
	)

	// When a cart ID first becomes available, flush any experiment variants that were
	// stored in localStorage before the cart existed (e.g. set on homepage impression
	// before the user had added any items).
	useEffect(() => {
		if (!cart?.id) return
		try {
			const stored = localStorage.getItem('bbx_exp_cart_attrs')
			if (!stored) return
			const attrs: Array<{ key: string; value: string }> = JSON.parse(stored)
			if (!attrs.length) return
			fetcher({
				query: updateCartAttributes,
				variables: { cartId: cart.id, attributes: attrs }
			})
				.then(() => localStorage.removeItem('bbx_exp_cart_attrs'))
				.catch((err) => console.error('Unable to flush experiment cart attributes:', err))
		} catch {
			// ignore localStorage errors
		}
	}, [cart?.id])

	// Writes an A/B experiment variant to the Shopify cart as a note_attribute so it
	// carries through to the order. If no cart exists yet, queues it in localStorage
	// and the effect above will flush it once the cart is created.
	// Key convention: exp_<experiment_name>  (e.g. exp_homepage_cta)
	// Value convention: "<variant> — <destination>" for human readability in Shopify admin
	const addExperimentVariantToCartCallback = useCallback(
		async (key: string, value: string) => {
			const attribute = { key, value }

			if (cart?.id) {
				try {
					await fetcher({
						query: updateCartAttributes,
						variables: { cartId: cart.id, attributes: [attribute] }
					})
				} catch (err) {
					console.error('Unable to add experiment variant to Shopify cart attributes:', err)
				}
			} else {
				// No cart yet — queue for when cart is created
				try {
					const existing: Array<{ key: string; value: string }> = JSON.parse(
						localStorage.getItem('bbx_exp_cart_attrs') || '[]'
					)
					const updated = [...existing.filter((a) => a.key !== key), attribute]
					localStorage.setItem('bbx_exp_cart_attrs', JSON.stringify(updated))
				} catch {
					// ignore localStorage errors
				}
			}

			return null
		},
		[cart?.id]
	)

	const addDiscountCodeCallback = useCallback(
		async (addCode: string) => {
			let applied = false
			let errors: any = null

			await mutate(
				async (currentValue) => {
					if (!currentValue) {
						return { cart: null, items: null, itemCount: 0 }
					}

					const freeVariants = await shouldUseDiscount({
						productsInCart: items || [],
						code: addCode
					})

					if (freeVariants) {
						// We'll assume they want us to add the first product to the cart; there's no really good solution
						// Accept for a product selector, though that's a bit overkill for this

						// Variants sorted from cheapest to most expensive
						const sortedVariants = freeVariants.sort(
							(a: any, b: any) => parseFloat(a.price) - parseFloat(b.price)
						)

						// Basically find the first variant that's not $0
						// In the future, we might consider accounting for variants that are in stock
						const variantToAdd = sortedVariants.find(
							(variant: any) => parseFloat(variant.price) !== 0
						)

						const alreadyInCart = currentValue.items?.some(
							(line: any) => line.merchandise.id === variantToAdd?.id
						)

						if (variantToAdd && !alreadyInCart) {
							await addItem({
								variantId: variantToAdd.id,
								quantity: 1
							})
						}
					}

					// Try discount codes first
					let discountResult = null
					let giftCardResult = null
					const allErrors: any[] = []

					try {
						discountResult = await updateCartDiscountCodes([...appliedDiscountCodes, addCode])

						if (discountResult.userErrors.length > 0) {
							allErrors.push(...discountResult.userErrors)
						}
					} catch (discountError) {
						// If discount codes fail, try gift card codes
					}

					// Check if discount code was successfully applied
					const discountCodeApplied = discountResult?.cart.discountCodes.find(
						(d) => d.code === addCode
					)?.applicable

					// If discount codes didn't work or had errors, try gift card codes
					if (
						(!discountCodeApplied ||
							(discountResult?.userErrors && discountResult.userErrors.length > 0)) &&
						!cart?.appliedGiftCards?.length
					) {
						try {
							giftCardResult = await updateCartGiftCardCodes([addCode])

							if (giftCardResult.userErrors.length > 0) {
								allErrors.push(...giftCardResult.userErrors)
							}
						} catch (giftCardError) {
							// If both fail, we'll handle it below
						}
					}

					// Determine which result to use and check if code was applied
					let updatedCart = null
					let codeApplied = false

					if (discountResult?.userErrors.length === 0) {
						updatedCart = discountResult.cart
						codeApplied = true
					} else if (giftCardResult && giftCardResult.userErrors.length === 0) {
						updatedCart = giftCardResult.cart
						// For gift cards, we need to check if it was applied successfully
						// This would depend on how gift cards are stored in the cart
						codeApplied = true // Assuming if no errors, it was applied
					}

					// If neither discount nor gift card worked, show generic error
					if (!codeApplied && allErrors.length === 0) {
						errors = [
							{
								code: 'INVALID_CODE',
								field: ['code'],
								message: 'Enter a valid discount code or gift card'
							}
						]
					} else if (allErrors.length > 0) {
						// Remove duplicate errors based on message
						const uniqueErrors = allErrors.filter(
							(err, index, self) => index === self.findIndex((e) => e.message === err.message)
						)
						errors = uniqueErrors
					}

					applied = codeApplied

					return formatCart({ cart: updatedCart })
				},
				{
					rollbackOnError: true
				}
			)

			if (errors) {
				console.error(errors)
			}

			return { applied, errors } as {
				applied: boolean
				errors: GetCartDiscountCodesUpdateReturn['userErrors'] | null
			}
		},
		[mutate, items, appliedDiscountCodes]
	)

	const removeDiscountCodeCallback = useCallback(
		async (removeCode: string) => {
			await mutate(
				async (currentValue) => {
					if (!currentValue) {
						return { cart: null, items: null, itemCount: 0 }
					}

					const { cart: updatedCart, userErrors } = await updateCartDiscountCodes(
						appliedDiscountCodes.filter((code) => code !== removeCode)
					)

					if (userErrors.length > 0) {
						console.error(userErrors)
					}

					return formatCart({ cart: updatedCart })
				},
				{
					rollbackOnError: true
				}
			)
		},
		[mutate, appliedDiscountCodes]
	)

	const value = useMemo(
		() => ({
			cart,
			items,
			itemCount,
			loading,
			revalidate: mutate,
			appliedDiscountCodes,
			appliedGiftCards: cart?.appliedGiftCards || [],
			discountAllocations: cart?.discountAllocations || [],
			addDiscountCode: addDiscountCodeCallback,
			removeDiscountCode: removeDiscountCodeCallback,
			getCart: getCartCallback,
			addSurveyAnswerToCartAttributes: addSurveyAnswerToCartAttributesCallback,
			addExperimentVariantToCart: addExperimentVariantToCartCallback,
			isCartOpen,
			openCart,
			closeCart,
			toggleCart
		}),
		[
			cart,
			items,
			itemCount,
			loading,
			mutate,
			appliedDiscountCodes,
			addDiscountCodeCallback,
			removeDiscountCodeCallback,
			getCartCallback,
			addSurveyAnswerToCartAttributesCallback,
			addExperimentVariantToCartCallback,
			isCartOpen,
			openCart,
			closeCart,
			toggleCart
		]
	)

	return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export default function useCart() {
	const context = useContext(CartContext)
	if (context === undefined) {
		throw new Error('useCart must be used within a CartProvider')
	}
	return context
}

export const formatCart = async (cartData: any): Promise<GetCartReturn> => {
	if (cartData?.cart) {
		const cartProducts = cartData.cart.lines.edges

		const normalizedProducts = cartProducts.map((product: any) => normalizeCartItem(product))

		const normalizedCart = normalizeCart(cartData.cart)

		// Total number of items in cart
		const newItemCount = normalizedProducts.reduce(
			(acc: number, curr: CartItem) => (curr.quantity ? acc + curr.quantity : acc),
			0
		)

		return { cart: normalizedCart, items: normalizedProducts, itemCount: newItemCount }
	}

	// If the cart does not exist, destroy it
	// https://community.shopify.com/c/storefront-api-and-sdks/storefront-api-cart-completedat-property/td-p/1399234
	// The cart will be null if it does not exist
	await destroyCart()

	return { cart: null, items: null, itemCount: 0 }
}

async function updateCartDiscountCodes(discountCodes: string[]) {
	const cartId = getCartId()

	const responseData: { cartDiscountCodesUpdate: GetCartDiscountCodesUpdateReturn } = await fetcher(
		{
			query: cartDiscountCodesUpdateMutation,
			variables: { cartId, discountCodes }
		}
	)

	return responseData.cartDiscountCodesUpdate
}

async function updateCartGiftCardCodes(giftCardCodes: string[]) {
	const cartId = getCartId()

	const responseData: { cartGiftCardCodesUpdate: GetCartGiftCardCodesUpdateReturn } = await fetcher(
		{
			query: cartGiftCardCodesUpdateMutation,
			variables: { cartId, giftCardCodes }
		}
	)

	return responseData.cartGiftCardCodesUpdate
}
